import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Divider,
  Stack,
  TextField,
  InputAdornment,
  Tooltip,
  Snackbar,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';

import {
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  LocalOfferOutlined as TagIcon,
  Close as CloseIcon,
  WarningAmber as EmptyIcon,
  ArrowBack as ArrowBackIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { portant, mono } from '../../theme/portantTheme';
import { insertAtCursor, copyText } from '../../taskpane';
import SignatureFields from './SignatureFields';
import InsertHint from '../Onboarding/InsertHint';

interface SourceField {
  fieldId: string;
  fieldName: string;
  fieldType: string;
  customMapping?: string;
}

interface Props {
  workflowName: string;
  selectedWorkfloFields: SourceField[];
  onBack: () => void;
}

const WorkflowDetailTaskpane: React.FC<Props> = ({
  workflowName,
  selectedWorkfloFields,
  onBack
}) => {
  const [tab, setTab] = useState<'tags' | 'signatures'>('tags');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [insertedTag, setInsertedTag] = useState<string | null>(null);
  const [copiedTag, setCopiedTag] = useState<string | null>(null);
  const [toast, setToast] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  // Directly filter the props data
  const filteredFields = useMemo(() => {
    if (!searchTerm) return selectedWorkfloFields || [];
    return selectedWorkfloFields.filter(
      (f) =>
        f.fieldName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.fieldId.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [selectedWorkfloFields, searchTerm]);

  const handleInsert = async (fieldName: string) => {
    const tag = `{{${fieldName}}}`;
    const ok = await insertAtCursor(tag);

    if (ok) {
      setInsertedTag(fieldName);
      setTimeout(() => setInsertedTag(null), 1500);
    } else {
      // Insert failed (e.g. no cursor in the document); fall back to copy so
      // the user is never stuck.
      await copyText(tag);
      setToast({
        open: true,
        message: "Couldn't insert at the cursor, so the tag was copied instead.",
      });
    }
  };

  const handleCopy = async (fieldName: string) => {
    await copyText(`{{${fieldName}}}`);
    setCopiedTag(fieldName);
    setTimeout(() => setCopiedTag(null), 1500);
  };

  return (
    <Box sx={{ bgcolor: portant.canvas, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>

      {/* HEADER with Back Button */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{ p: 1, bgcolor: portant.white, borderBottom: `1px solid ${portant.parchment}` }}
      >
        <IconButton onClick={onBack} size="small" aria-label="Back">
            <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle2" sx={{ flexGrow: 1, textAlign: 'center', pr: 4, fontSize: '14px' }}>
          {workflowName || 'Untitled Workflow'}
        </Typography>
      </Stack>

      {/* TABS */}
      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        variant="fullWidth"
        sx={{ bgcolor: portant.white, borderBottom: `1px solid ${portant.parchment}`, minHeight: 40 }}
      >
        <Tab value="tags" label="Merge tags" sx={{ fontSize: '12px', minHeight: 40, textTransform: 'none' }} />
        <Tab value="signatures" label="Signature fields" sx={{ fontSize: '12px', minHeight: 40, textTransform: 'none' }} />
      </Tabs>

      {tab === 'signatures' ? (
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          <InsertHint flow="signature" />
          <SignatureFields onNotice={(message) => setToast({ open: true, message })} />
        </Box>
      ) : (
      <>
      {/* HOW-TO ANIMATION */}
      <InsertHint flow="tag" />

      {/* SEARCH BAR */}
      <Box sx={{ px: 2, mb: 1 }}>
        {!showSearch ? (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="overline" sx={{ color: portant.orange }}>Source Fields</Typography>
            <IconButton size="small" onClick={() => setShowSearch(true)} aria-label="Search fields">
              <SearchIcon fontSize="small" />
            </IconButton>
          </Stack>
        ) : (
          <TextField
            autoFocus
            fullWidth
            size="small"
            variant="standard"
            placeholder="Search fields..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: (
                <IconButton size="small" onClick={() => { setShowSearch(false); setSearchTerm(''); }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              ),
            }}
          />
        )}
      </Box>

      <Divider />

      {/* FIELD LIST */}
      <List sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {filteredFields.length > 0 ? (
          filteredFields.map((field) => (
            <ListItem
              key={field.fieldId}
              divider
              secondaryAction={
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Button
                    size="small"
                    variant={insertedTag === field.fieldName ? 'contained' : 'outlined'}
                    color="primary"
                    onClick={() => handleInsert(field.fieldName)}
                    sx={{ fontSize: '11px', height: '1.875rem', px: 1.25, minWidth: 76 }}
                  >
                    {insertedTag === field.fieldName ? 'Inserted' : 'Insert'}
                  </Button>
                  <Tooltip title={copiedTag === field.fieldName ? 'Copied' : 'Copy tag'} arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(field.fieldName)}
                      aria-label="Copy tag"
                      sx={{ color: copiedTag === field.fieldName ? portant.blue : portant.charred }}
                    >
                      <CopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
              sx={{ borderColor: portant.notebook }}
            >
              <ListItemIcon sx={{ minWidth: 34 }}>
                {field.fieldType === 'DATE' ? (
                  <CalendarIcon fontSize="small" sx={{ color: portant.toasted }} />
                ) : (
                  <TagIcon fontSize="small" sx={{ color: portant.toasted }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={field.fieldName}
                secondary={`{{${field.fieldName}}}`}
                primaryTypographyProps={{ fontSize: '12.5px', fontWeight: 600 }}
                secondaryTypographyProps={{
                  fontSize: '10.5px',
                  fontFamily: mono,
                  color: portant.charred,
                  sx: { overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
              />
            </ListItem>
          ))
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <EmptyIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
            <Typography fontSize="13px" color="text.secondary">No fields found</Typography>
          </Box>
        )}
      </List>
      </>
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkflowDetailTaskpane;
